import {call_function} from "@cling/lib.shared.faas/index"
import {log} from "@cling/lib.shared.logging/index"
import {
    AccountUID,
    account_uid_from_gip_uid,
    as_language,
    is_Language,
    Locale,
    ManageAuthRequest,
    ManageAuthResponse,
    ManageAuthSendResetPasswordMessage,
    ManageAuthSendVerifyEmailMessage,
    OrganizationUID,
    SignUpRequest,
    SourceCampaign,
    TeamCodeUID,
} from "@cling/lib.shared.model/model"
import {to_b64url} from "@cling/lib.shared.model/model_utils"
import {assert_never, not_null} from "@cling/lib.shared.utils/index"
import {safe_session_storage} from "@cling/lib.web.utils/safe_storage"
import {
    createUserWithEmailAndPassword,
    FacebookAuthProvider,
    getAdditionalUserInfo,
    getRedirectResult,
    GoogleAuthProvider,
    signInWithEmailAndPassword,
    signInWithRedirect,
} from "firebase/auth"
import {auth, LAST_LOCATION_KEY} from "./gip"
import {LoginError, LoginErrorCode, SignupError, SignupErrorCode} from "./types"
import {OAuthProvider} from "firebase/auth"

const signup_error_map: Record<string, SignupErrorCode> = {
    "auth/email-already-in-use": "user_exists",
    "auth/wrong-password": "invalid_password",
    "auth/invalid-email": "invalid_signup",
    "auth/weak-password": "password_strength_error",
}

const login_error_map: Record<string, LoginErrorCode> = {
    "auth/user-not-found": "access_denied",
    "auth/wrong-password": "access_denied",
    "auth/unverified-email": "unverified_email",
}

export async function sign_up(
    args: {
        is_dev: boolean
        invite_code?: string
        team_code_uid?: TeamCodeUID
        source_campaign?: SourceCampaign
        create_organization_uid?: OrganizationUID
        locale: Locale
    } & (
        | {provider: "google" | "facebook" | "apple"}
        | {
              provider: "password"
              email: string
              password: string
              given_name: string
              family_name: string
          }
    ),
) {
    const sign_up_request = new SignUpRequest({
        create_organization_uid: args.create_organization_uid,
        invite_code: args.invite_code,
        source_campaign: args.source_campaign,
        team_code_uid: args.team_code_uid,
    })
    if (args.provider === "password") {
        const {email, password} = args
        try {
            log.info("Signing up with email+password ...")
            await createUserWithEmailAndPassword(auth, email, password)
            log.info("Signed up with email+password - sending verification e-mail ...")
            sign_up_request.family_name = args.family_name
            sign_up_request.given_name = args.given_name
            sign_up_request.locale = args.locale
            const res = await call_function(
                new ManageAuthRequest({
                    send_verify_email_message: new ManageAuthSendVerifyEmailMessage({
                        email,
                        sign_up_request,
                        language: is_Language(args.locale) ? as_language(args.locale) : "en",
                    }),
                }),
                ManageAuthResponse,
            )
            if (window._under_test) {
                // Store `login_url` so `playwright_commands#signup` can pick it up.
                const login_url = res.test_only_verify_url
                safe_session_storage.setItem("__test_only_signup_login_url", login_url)
                log.debug("Running in test, storing login-url in localStorage", {login_url})
            }
            log.info("Signed up with email+password - all done")
        } catch (error) {
            throw new SignupError(signup_error_map[error.code] || "other", error)
        }
    } else if (
        args.provider === "google" ||
        args.provider === "facebook" ||
        args.provider === "apple"
    ) {
        log.debug("Signing up with social auth provider", {provider: args.provider})
        safe_session_storage.setItem("signup.request", to_b64url(sign_up_request))
        try {
            await sign_in_with_social(args)
        } catch (error) {
            throw new SignupError(signup_error_map[error.code] || "other", error)
        }
    } else {
        throw assert_never(args.provider)
    }
}

export async function reset_password({
    email,
    locale,
}: {
    is_dev: boolean
    email: string
    locale: Locale
}) {
    await call_function(
        new ManageAuthRequest({
            send_reset_password_message: new ManageAuthSendResetPasswordMessage({
                email,
                language: is_Language(locale) ? as_language(locale) : "en",
            }),
        }),
    )
}

export async function log_in(
    args: {is_dev: boolean; locale: Locale} & (
        | {provider: "google" | "facebook" | "apple"}
        | {provider: "password"; email: string; password: string}
    ),
) {
    if (args.provider === "google" || args.provider === "facebook" || args.provider === "apple") {
        await sign_in_with_social(args)
    } else if (args.provider === "password") {
        let res
        try {
            res = await signInWithEmailAndPassword(auth, args.email, args.password)
        } catch (error) {
            throw new LoginError(login_error_map[error.code] || "other", error)
        }
        if (!res.user.emailVerified) {
            throw new LoginError("unverified_email")
        }
        return {
            access_token: await res.user.getIdToken(),
            account_uid: account_uid_from_gip_uid(res.user.uid),
            is_signup: res.user.metadata.lastSignInTime === res.user.metadata.creationTime,
        }
    } else {
        throw assert_never(args.provider)
    }
}

export async function login_or_signup_redirected(): Promise<
    | undefined
    | {is_error: true; error: any}
    | {
          is_error: false
          access_token: string
          given_name?: string
          family_name?: string
          profile_image_url?: string
          locale?: string
          account_uid: AccountUID
      }
> {
    if (!navigator.onLine) {
        log.info(
            "[auth] We are offline - `login_or_signup_redirected` will treat the request as not an auth redirect",
        )
        return
    }
    try {
        const redirect_result = await getRedirectResult(auth)
        if (!redirect_result) {
            return undefined
        }
        const user_info = not_null(
            getAdditionalUserInfo(redirect_result),
            "`getRedirectResult()` returned with no additional user-info",
        )
        const profile = (user_info as any).profile // Google and Facebook
        const token_res = (redirect_result as any)._tokenResponse // Apple
        let given_name = profile.given_name || profile.first_name || token_res?.firstName
        if (profile.middle_name) {
            given_name += ` ${profile.middle_name}`
        }
        return {
            given_name,
            family_name: profile.family_name || profile.last_name || token_res?.lastName,
            profile_image_url: profile.picture,
            locale: profile.locale,
            account_uid: account_uid_from_gip_uid(redirect_result.user.uid),
            access_token: await redirect_result.user.getIdToken(),
            is_error: false,
        }
    } catch (error) {
        return {
            is_error: true,
            error,
        }
    }
}

async function sign_in_with_social({
    provider,
    locale,
}: {
    provider: "google" | "facebook" | "apple"
    locale: Locale
}) {
    // Trick Firebase Auth into redirecting to the correct page.
    history.replaceState({}, "", safe_session_storage.getItem(LAST_LOCATION_KEY) || "/c")
    await signInWithRedirect(
        auth,
        provider === "google"
            ? google_provider()
            : provider === "apple"
              ? apple_provider(locale)
              : new FacebookAuthProvider(),
    )
}

function google_provider() {
    const provider = new GoogleAuthProvider()
    provider.addScope("profile")
    provider.addScope("email")
    return provider
}

function apple_provider(locale: Locale) {
    const provider = new OAuthProvider("apple.com")
    provider.addScope("email")
    provider.addScope("name")
    provider.setCustomParameters({locale})
    return provider
}
